/** Small, subtle "linked to HubSpot" indicator (Demo Mode only, conceptual CRM integration - see
 * `mocks/data/customerManagementData.ts`). No approved HubSpot logo asset exists in this
 * repository yet (checked `src/assets/index.ts`), so this intentionally falls back to a small,
 * brand-tinted CRM link icon rather than reproducing HubSpot's trademarked logo - per the
 * "fall back to a small CRM link icon if the HubSpot logo asset is unavailable" requirement.
 * Reused by the Contacts list (clickable, opens the linked HubSpot contact record) and the
 * HubSpot summary card (decorative, non-clickable) so linked-status never relies on colour
 * alone - both usages always pair the icon with visible text ("Linked to HubSpot" tooltip or the
 * "HubSpot" card title). */

import { IconButton, SvgIconProps, Tooltip } from '@mui/material';
import { Link as LinkIcon } from '@mui/icons-material';

export const HUBSPOT_BRAND_COLOR = '#FF7A59';

interface HubSpotIndicatorProps {
  /** When provided, the icon becomes a clickable, keyboard-accessible button that opens this
   * HubSpot record URL in a new tab. When omitted, the icon is purely decorative. */
  url?: string;
  ariaLabel?: string;
  size?: SvgIconProps['fontSize'];
}

const HubSpotIndicator = ({ url, ariaLabel, size = 'small' }: HubSpotIndicatorProps) => {
  if (!url) {
    return (
      <LinkIcon
        aria-hidden
        fontSize={size}
        sx={{ color: HUBSPOT_BRAND_COLOR }}
      />
    );
  }

  return (
    <Tooltip title="Linked to HubSpot">
      <IconButton
        size="small"
        aria-label={ariaLabel ?? 'Open linked HubSpot record'}
        onClick={(event) => {
          event.stopPropagation();
          window.open(url, '_blank', 'noopener,noreferrer');
        }}
        sx={{ color: HUBSPOT_BRAND_COLOR, p: 0.25 }}
      >
        <LinkIcon fontSize={size} />
      </IconButton>
    </Tooltip>
  );
};

export default HubSpotIndicator;
